import {
  AgileEngineRevenue,
  ClientGrowth,
} from '../components/revenueStatusChip/RevenueStatusChip';
import {
  QuarterlyRevenueLocation,
  QuarterlyRevenueSeniorities,
  Seniority,
} from './accounts';

export enum Mode {
  EDIT = 'EDIT',
  VIEW = 'VIEW',
}

export type QuarterlyRevenueAccounts = QuarterlyRevenueAccount[];

export interface QuarterlyRevenueAccountsResponse {
  currentPage: number;
  pageSize: number;
  quarterlyRevenueStatus: QuarterlyRevenueAccounts;
  totalItems: number;
  totalPages: number;
}

export interface QuarterlyRevenueAccount {
  accountId: string;
  accountName: string;
  headCount: number;
  pdmId: string;
  pdmFullName: string;
  dmId: string;
  dmFullName: string;
  statuses: QuarterlyRevenueAccountSummary[];
  summaryFilled: boolean;
  quarter: number;
  previousQuarterRevenue?: number | null;
  previousQuarterRevenueQuarter?: number | null;
}

export enum GrowthSegmentation {
  KEEP_STABLE = 'KEEP_STABLE',
  DERISK = 'DERISK',
  EXPAND = 'EXPAND',
}

export const GrowthSegmentationLabels = {
  [GrowthSegmentation.KEEP_STABLE]: 'Keep stable',
  [GrowthSegmentation.DERISK]: 'Derisk',
  [GrowthSegmentation.EXPAND]: 'Expand',
};

export const GrowthSegmentationLabelsToValues = {
  ['Keep stable']: GrowthSegmentation.KEEP_STABLE,
  ['Derisk']: GrowthSegmentation.DERISK,
  ['Expand']: GrowthSegmentation.EXPAND,
};

export const GrowthSegmentationValues = Object.values(GrowthSegmentation);

export interface QuarterlyRevenueAccountSummary {
  id: string;
  accountId: string;
  accountName: string;
  quarter: number;
  expectedRevenue?: number | null;
  previousQuarterRevenue?: number | null;
  previousQuarterRevenueQuarter?: number | null;

  aeRevenueDecreaseReasons: AERevenueDecreaseReasons[];
  agileEngineRevenue: AgileEngineRevenue;

  clientGrowth: ClientGrowth;
  growthSegmentation: GrowthSegmentation | null;
  growthSegmentationValue: number | null;

  overview: string;
  forecast: string;
  growthActions: string;

  hcDelta: number;
  headCount: number;

  locations: QuarterlyRevenueLocation[];
  seniorities: QuarterlyRevenueSeniorities[];
  techFlows: string[];

  affectedPeople?: string[];
}

export interface Project {
  id: string;
  name: string;
  membersCount: number;
  pastRevenue: number;
  grossMargin: number;
  netMargin: number;
  expectedMembersCount: number;
  startedAt: string;
  endedAt: string;
  membersWithSowToExpireWithinMonth: number;
  sowStatus: string;
  revenueLastInvoiceDate: string;
}

export interface QuarterlyRevenue {
  id: string;
  accountId: string;
  expectedRevenue?: number | null;
  aeRevenueDecreaseReasons: AERevenueDecreaseReasons[];
  agileEngineRevenue: AgileEngineRevenue;
  clientGrowth: ClientGrowth;
  quarter: Quarter;
  overview: string;
  forecast: string;
  growthActions: string;
  pdms: string | null;
  hcDelta: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type QuarterlyRevenueSummary = {
  id?: string;
  accountId: string;
  quarter: number;
  expectedRevenue?: number | null;
  headCount?: number;
  aeRevenueDecreaseReasons?: AERevenueDecreaseReasons[];
  agileEngineRevenue: AgileEngineRevenue;
  clientGrowth: ClientGrowth;
  overview?: string;
  pdms?: string;
  forecast?: string;
  growthActions?: string;
  hcDelta?: number;
  affectedPeople?: string[];
  legalLocation?: QuarterlyRevenueLocation[];
  seniority?: Seniority[];
  techFlow?: string[];
  updatedAt: string;
  source?: string;
};

export interface GetRevenuePagePayloadType {
  currentPage?: number;
  pageSize?: number;
  filters?: FilterDto;
  quarter?: number;
}

export enum Quarter {
  Q1 = 'Q1',
  Q2 = 'Q2',
  Q3 = 'Q3',
  Q4 = 'Q4',
}

export enum AERevenueDecreaseReasons {
  CLIENT_FINANCIAL_ISSUES = 'Client Financial Issues',
  IMPORTANT_CLIENT_STAKEHOLDER_CHANGES = 'Important Client Stakeholder Changes',
  AE_TEAM_PERFORMANCE_ISSUES = 'AE Team Performance Issues',
  PROJECT_DELIVERY_ISSUES = 'Project Delivery Issues',
  AE_TEAM_ATTRITION = 'AE Team Attrition',
  CHALLENGES_IN_TEAM_BUILD_UP_SLOW_RECRUITING = 'Challenges in Team Build-Up / Slow Recruiting',
  CLIENT_STRATEGY_CHANGE = 'Client Strategy Change',
  THE_END_OF_PROJECT = 'The End of Project',
  OTHER = 'Other',
}
export type QuarterlyRevenueFilter = {
  clientGrowth: string;
  hcDeltaType: string;
  nextQRevenueStatus: string;
  PDMsToFilter: string[];
  DMsToFilter: string[];
};
export interface QuarterlyRevenueApiPayload {
  currentPage: number;
  pageSize: number;
  filters: {
    nextQRevenueStatus: string;
    clientGrowth: string;
    hcDeltaType: string;
  };
  quarter?: number;
}

export interface ExportDataRow {
  account: { name: string };
  quarter: number;
  expectedRevenue?: number | null;

  agileEngineRevenue: AgileEngineRevenue;
  clientGrowth: ClientGrowth;
  aeRevenueDecreaseReasons: AERevenueDecreaseReasons[];

  overview: string;
  forecast: string;
  growthActions: string;

  hcDelta: number;
  pdmId: string;
  pdmFullName: string;
  dmId: string;
  dmFullName: string;
  headCount: number;
}
export class FilterDto {
  nextQRevenueStatus?: string;
  clientGrowth?: string;
  aeRevenueDecreaseReasons?: string;
  hcDeltaType?: string;
  pdmId?: string[];
  dmId?: string[];
}

export interface PostQuarterlyRevenueStatusDto {
  accountId: string;
  quarter: number;
  expectedRevenue?: number | null;
  agileEngineRevenue: AgileEngineRevenue;
  clientGrowth: ClientGrowth;
  aeRevenueDecreaseReasons?: string[];
  overview?: string;
  forecast?: string;

  growthActions?: string;

  hcDelta: number;

  growthSegmentation?: GrowthSegmentation;

  growthSegmentationValue?: number;

  locations?: QuarterlyRevenueLocation[];

  seniorities?: QuarterlyRevenueSeniorities[];

  techFlows?: string[];

  affectedPeople?: string[];
  source?: string;
}

// --- --- --- --- --- PUT /quarterly-revenue-status/update --- --- --- --- --- //
export interface PutQuarterlyRevenueStatusDto
  extends Pick<
    PostQuarterlyRevenueStatusDto,
    | 'accountId'
    | 'quarter'
    // agileEngineRevenue
    // clientGrowth
    | 'aeRevenueDecreaseReasons'
    | 'overview'
    | 'forecast'
    | 'growthActions'
    // hcDelta
    | 'growthSegmentation'
    | 'growthSegmentationValue'
    | 'locations'
    | 'seniorities'
    | 'techFlows'
    | 'affectedPeople'
    | 'expectedRevenue'
  > {
  agileEngineRevenue?: AgileEngineRevenue;
  clientGrowth?: ClientGrowth;
  hcDelta?: number;
  affectedPeople?: string[];
  source?: string;
}
